<template>
  <div class="login">
        <HeaderCentered>
            <template #title>
                <h1>{{ $t('login.login.title') }}</h1>
                <h1>{{ $t('login.login.subtitle') }}</h1>
            </template>
            <template #subtitle>
                <h5 v-if="error" class="warning remove-margin-bottom">
                    {{ $t(error, {attribute: $t('dashboard.user.tfa.setup.attributes.password')}) }}
                </h5>
            </template>
        </HeaderCentered>
        <OtCardContent>
            <CredentialsForm
                class="login__provider login__provider--credentials"
                v-if="showCredentials"
                @submit="submit"
                @input="onInput"
                :error="error"
                :submitting="submitting"
            />
            <SingleSignOn class="login__provider login__provider--sso" @use-sso="useSso" />
        </OtCardContent>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import HeaderCentered from './HeaderCentered.vue';
import OtButton from './OtButton.vue';
import SingleSignOn from './SingleSignOn.vue';
import CredentialsForm from './CredentialsForm.vue';
import {
    combineCredentialsActions,
    CREDENTIALS_ACTION,
    getCredentialsActionOverride,
    type SignInProvider,
} from '../services/util/signInAction';

@Component({
    components: {
        CredentialsForm,
        SingleSignOn,
        HeaderCentered,
        OtButton,
    },
})
export default class LoginForm extends Vue {

    @Prop({ default: null }) error!: string | null;

    @Prop({ default: false }) submitting!: boolean;

    email: string | null = null;

    password: string | null = null;

    get showCredentials(): boolean {
        // DD-4E89 - The login credentials form is shown by default, unless specifically hidden by (login/sso) settings.
        return combineCredentialsActions(
            this.$whitelabel.login.credentials?.action ?? '',
            getCredentialsActionOverride(), // query: login_credentials={action}
        ) !== CREDENTIALS_ACTION.HIDE;
    }

    submit(email: string, password: string): void {
        this.$emit('submit', email, password);
    }

    onInput(): void {
        this.$emit('input');
    }

    onChange(e: Event): void {
        if (e.target instanceof HTMLInputElement && e.target.value) {
            this.password = e.target.value;
        }
    }

    useSso(config: SignInProvider): void {
        this.$emit('use-sso', config);
    }

}
</script>

<style lang="scss" scoped>
.warning.remove-margin-bottom {
    margin-bottom: 0;
}

.warning {
    color: var(--ot-color-accent-orange-dark);
    text-align: center;
    margin-bottom: var(--ot-spacing-3xl);
}

.lost-password {
    color: var(--ot-color-core-brand);

    .ot-button {
        padding-bottom: 0;
    }
}

.signup {
    .ot-button {
        padding-top: 0;
        padding-bottom: 0;
        height: auto;
        color: var(--ot-color-core-brand);
        margin-left: .25rem;
    }
}

.ot-content input[type="email"]:invalid:not(:focus) {
    border: var(--ot-input-border);
    box-shadow: 0 0 0 1px var(--ot-input-border-color);
}

.ot-content input[type="email"]:invalid {
    background: var(--ot-input-background);
    color: var(--ot-input-color);
}

.footer {
    display: flex;
    margin-top: var(--ot-spacing-2xl);
    flex-wrap: wrap-reverse;
    gap: var(--ot-spacing-default);

    @media (min-width: 40rem) {
        flex-wrap: nowrap;
    }

    &__left, &__right {
        flex: auto;
    }
}

.login:has(.login__provider--credentials):has(.login__provider--sso > *) .login__provider:not(:last-child) {
    margin-bottom: var(--ot-spacing-lg);
    padding-bottom: var(--ot-spacing-lg);
    border-bottom: 1px solid var(--ot-color-core-accent-tertiary, hsl(0deg 0% 35% / 100%));
}
</style>
